(() => {
  /* ── Description must never repeat fabric/material/care facts ───────
     Those belong in Materials & Care. Liquid strips on first paint;
     this catches inline leftovers and templates that skip the snippet. */
  const stripDuplicateFactsFromDescription = () => {
    const FACT_ONLY =
      /^(fabric|content|material(?:\s+content)?|care(?:\s+instructions)?|origin|country of origin|made in)\s*:/i;
    const INLINE_FACT =
      /\s*(?:<strong>)?\s*(?:Fabric|Content|Material(?:\s+Content)?|Care(?:\s+Instructions)?|Origin|Country of Origin|Made in)\s*:\s*(?:<\/strong>)?\s*[^<]*/gi;

    const stripRoot = (root) => {
      if (!root) return;
      root.querySelectorAll('p, li').forEach((el) => {
        const text = (el.textContent || '').replace(/\s+/g, ' ').trim();
        if (!text) return;
        if (FACT_ONLY.test(text)) {
          el.remove();
          return;
        }
        INLINE_FACT.lastIndex = 0;
        if (!INLINE_FACT.test(el.innerHTML)) return;
        INLINE_FACT.lastIndex = 0;
        const next = el.innerHTML.replace(INLINE_FACT, '').replace(/\s+\./g, '.').trim();
        const leftover = next.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim();
        if (!leftover) el.remove();
        else el.innerHTML = next;
      });
    };

    document.querySelectorAll('.product-block--tab').forEach((block) => {
      const title = (block.querySelector('.collapsible-trigger')?.textContent || '')
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
      if (!/^description\b/.test(title)) return;
      stripRoot(block.querySelector('.collapsible-content__inner') || block);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', stripDuplicateFactsFromDescription);
  } else {
    stripDuplicateFactsFromDescription();
  }
})();

(() => {
  /* ── PDP exclusive accordion a11y sync ──────────────────────────────
     Motions closes sibling tabs via .collapsible-trigger--tab + data-id,
     but leaves aria-expanded stuck on the previous open trigger. */
  if (!window.djPdpAccordionA11yInitialized) {
    window.djPdpAccordionA11yInitialized = true;
    document.addEventListener('click', (event) => {
      const trigger = event.target.closest?.(
        '.product-block--tab .collapsible-trigger--tab'
      );
      if (!trigger?.dataset?.id) return;
      const group = trigger.dataset.id;
      requestAnimationFrame(() => {
        document
          .querySelectorAll(`.collapsible-trigger--tab[data-id="${group}"]`)
          .forEach((el) => {
            el.setAttribute(
              'aria-expanded',
              el.classList.contains('is-open') ? 'true' : 'false'
            );
          });
      });
    });
  }

  /* ── Smile.io: hide under drawers + How to Measure ────────────────── */
  if (!window.djSmileOverlayGuardInitialized) {
    window.djSmileOverlayGuardInitialized = true;

    const SMILE_SELECTORS = [
      '#smile-ui-container',
      '#smile-ui-lite-container',
      '.smile-launcher-frame-container',
      'iframe.smile-launcher-frame',
    ];

    const overlayOpen = () => {
      const html = document.documentElement;
      const body = document.body;
      const cart = document.getElementById('CartDrawer');
      return (
        html.classList.contains('dj-measure-open') ||
        html.classList.contains('js-drawer-open') ||
        body?.classList.contains('js-drawer-open') ||
        !!cart?.classList.contains('drawer--is-open')
      );
    };

    const syncSmileVisibility = () => {
      const hide = overlayOpen();
      SMILE_SELECTORS.forEach((sel) => {
        document.querySelectorAll(sel).forEach((el) => {
          if (hide) {
            if (el.dataset.djSmilePrevDisplay === undefined) {
              el.dataset.djSmilePrevDisplay = el.style.display || '';
            }
            el.style.setProperty('display', 'none', 'important');
            el.style.setProperty('visibility', 'hidden', 'important');
            el.style.setProperty('pointer-events', 'none', 'important');
            el.style.setProperty('opacity', '0', 'important');
          } else if (el.dataset.djSmilePrevDisplay !== undefined) {
            el.style.display = el.dataset.djSmilePrevDisplay;
            el.style.removeProperty('visibility');
            el.style.removeProperty('pointer-events');
            el.style.removeProperty('opacity');
            delete el.dataset.djSmilePrevDisplay;
          }
        });
      });
    };

    window.djSyncSmileVisibility = syncSmileVisibility;

    const observeTarget = (el) => {
      if (!el) return;
      new MutationObserver(syncSmileVisibility).observe(el, {
        attributes: true,
        attributeFilter: ['class'],
      });
    };

    observeTarget(document.documentElement);
    observeTarget(document.body);
    observeTarget(document.getElementById('CartDrawer'));

    /* Smile injects late — re-sync when its nodes appear */
    new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (
            node.id === 'smile-ui-container' ||
            node.id === 'smile-ui-lite-container' ||
            node.classList?.contains('smile-launcher-frame-container') ||
            node.querySelector?.('#smile-ui-container, .smile-launcher-frame-container')
          ) {
            syncSmileVisibility();
            observeTarget(document.getElementById('CartDrawer'));
            return;
          }
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });

    document.addEventListener('click', (event) => {
      if (
        event.target.closest?.(
          '.js-drawer-open-cart, .js-drawer-close, [aria-controls="CartDrawer"]'
        )
      ) {
        requestAnimationFrame(syncSmileVisibility);
        setTimeout(syncSmileVisibility, 50);
        setTimeout(syncSmileVisibility, 300);
      }
    });
  }

  /* ── How to Measure modal ─────────────────────────────────────────── */
  if (!window.djMeasureGuideInitialized) {
    window.djMeasureGuideInitialized = true;

    let activeModal = null;
    let previousFocus = null;
    let modalHome = null;

    const openModal = (modal, trigger, view = 'guide') => {
      if (!modal) return;
      activeModal = modal;
      previousFocus = trigger;
      modalHome = { parent: modal.parentNode, next: modal.nextSibling };
      document.body.appendChild(modal);
      modal.hidden = false;
      document.documentElement.classList.add('dj-measure-open');
      window.djSyncSmileVisibility?.();
      if (view === 'chart') {
        requestAnimationFrame(() => {
          modal
            .querySelector('[data-dj-measure-chart-inline]')
            ?.scrollIntoView({ block: 'start', behavior: 'smooth' });
        });
      } else {
        modal.querySelector('.dj-measure-modal__body')?.scrollTo({ top: 0 });
      }
      modal.querySelector('[data-dj-measure-close]')?.focus();
    };

    const closeModal = (modal = activeModal) => {
      if (!modal) return;
      modal.hidden = true;
      document.documentElement.classList.remove('dj-measure-open');
      window.djSyncSmileVisibility?.();
      if (modalHome?.parent) {
        if (modalHome.next && modalHome.next.parentNode === modalHome.parent) {
          modalHome.parent.insertBefore(modal, modalHome.next);
        } else {
          modalHome.parent.appendChild(modal);
        }
      }
      modalHome = null;
      activeModal = null;
      previousFocus?.focus();
      previousFocus = null;
    };

    document.addEventListener('click', (event) => {
      const open = event.target.closest('[data-dj-measure-open]');
      if (open) {
        event.preventDefault();
        const view = open.dataset.djMeasureView === 'chart' ? 'chart' : 'guide';
        openModal(document.getElementById(open.dataset.djMeasureOpen), open, view);
        return;
      }

      const close = event.target.closest('[data-dj-measure-close]');
      if (close) {
        event.preventDefault();
        closeModal(close.closest('[data-dj-measure-modal]'));
      }
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && activeModal) closeModal(activeModal);
    });
  }

  /* ── Specs follow selected size/variant ───────────────────────────── */
  if (window.djVariantSpecsInitialized) return;
  window.djVariantSpecsInitialized = true;

  const POM_LABELS =
    /^(Waist|Hip(?:\s*\(@|\s*:)|Front Rise|Back Rise|Leg Opening|Sweep|Inseam|Full Inseam|Length)\b/i;

  const PRIORITY = {
    waist: 10,
    hip: 20,
    'front rise': 30,
    'back rise': 40,
    'leg opening': 50,
    sweep: 50,
    inseam: 60,
    'full inseam': 60,
    length: 60
  };

  const escapeHtml = (value) =>
    String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const splitOutsideParens = (spec) => {
    const result = [];
    let current = '';
    let level = 0;
    for (let i = 0; i < spec.length; i++) {
      const char = spec[i];
      if (char === '(') level++;
      else if (char === ')') level = Math.max(0, level - 1);
      if (char === ',' && level === 0) {
        if (current.trim()) result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    if (current.trim()) result.push(current.trim());
    return result;
  };

  const looksLikeMeasurementsOnly = (spec) =>
    /^(Waist|Hip|Front Rise|Leg Opening|Full Inseam|Length|Sweep)\s*:/i.test(spec.trim());

  const extractSpecsPart = (raw, hasMetafieldMaterials) => {
    let safe = (raw || '').trim().replace(/,\s*$/, '');
    if (!safe || safe.toLowerCase() === 'n/a') return '';
    safe = safe.replace(/^material:\s*/i, '');

    if (looksLikeMeasurementsOnly(safe)) return safe;

    const startParen = safe.indexOf('(');
    if (startParen !== -1) {
      const endParen = safe.indexOf(')', startParen);
      if (endParen !== -1) {
        return safe
          .slice(endParen + 1)
          .replace(/^,\s*/, '')
          .trim();
      }
    }

    const pomMatch = safe.match(
      /,\s*(?=(?:Front Rise|Back Rise|Waist|Hip(?:\s*\(@|\s*:)|Leg Opening|Inseam|Full Inseam|Length|Sweep)\s*:)/i
    );
    if (pomMatch && pomMatch.index != null) {
      return safe.slice(pomMatch.index + pomMatch[0].length).trim();
    }

    // No POM labels — nothing to show under Specifications when materials live elsewhere.
    if (hasMetafieldMaterials) return '';
    const commaIndex = safe.indexOf(',');
    return commaIndex === -1 ? '' : safe.slice(commaIndex + 1).trim();
  };

  const normalizeTokens = (specsPart) => {
    if (!specsPart) return [];
    return splitOutsideParens(specsPart)
      .map((t) => t.trim())
      .filter((t) => {
        if (!t) return false;
        if (/^imported$/i.test(t)) return false;
        if (/%/.test(t) && !t.includes(':')) return false;
        if (/^material\s*:/i.test(t)) return false;
        return true;
      })
      .map((token) => {
        if (!token.includes(':')) return token;
        const label = token.split(':')[0].trim();
        let value = token.split(':').slice(1).join(':').trim();
        if (/^front\s*rise$/i.test(label)) {
          value = value.replace(/\s*\(\s*include\s+waistband\s*\)\s*/gi, ' ').trim();
        }
        return `${label}: ${value}`;
      })
      .map((t, i) => {
        let p = 900;
        if (t.includes(':')) {
          const label = t.split(':')[0].trim().toLowerCase();
          if (label === 'hip' || /^hip\b/.test(label)) p = 20;
          else p = PRIORITY[label] ?? 100;
        } else if (/zip\s*fly|button\s*closure/i.test(t)) p = 800;
        else if (/pocket/i.test(t)) p = 850;
        return { t, i, p };
      })
      .sort((a, b) => a.p - b.p || a.i - b.i)
      .map(({ t }) => t);
  };

  const renderSpecList = (listEl, tokens) => {
    if (!listEl) return;
    if (!tokens.length) {
      listEl.innerHTML = '<li><span class="dj-spec-list__value">No specifications available.</span></li>';
      return;
    }
    listEl.innerHTML = tokens
      .map((token) => {
        if (!token.includes(':')) {
          return `<li><span class="dj-spec-list__value">${escapeHtml(token)}</span></li>`;
        }
        const label = token.split(':')[0].trim();
        const value = token.split(':').slice(1).join(':').trim();
        return `<li><span class="dj-spec-list__label">${escapeHtml(label)}:</span><span class="dj-spec-list__value">${escapeHtml(value)}</span></li>`;
      })
      .join('');
  };

  const getVariants = (section) => {
    const el = section?.querySelector('[data-variant-json]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent);
    } catch {
      return [];
    }
  };

  const getSelectedOptions = (section) => {
    const selected = {};
    section.querySelectorAll('[data-variant-input]').forEach((el) => {
      const type = el.getAttribute('type');
      if ((type === 'radio' || type === 'checkbox') && !el.checked) return;
      if (el.dataset.index) selected[el.dataset.index] = el.value;
    });
    return selected;
  };

  const findVariant = (variants, selected) => {
    const keys = Object.keys(selected);
    const full = variants.find((v) => keys.every((k) => v[k] === selected[k]));
    if (full) return full;

    // Spec is a linked third option — size/color alone should still resolve a row.
    const withoutSpec = keys.filter((k) => {
      // Prefer matching on non-Spec options when a full match fails.
      const sample = selected[k] || '';
      return !POM_LABELS.test(sample) && !/^material:/i.test(sample);
    });
    if (withoutSpec.length) {
      return (
        variants.find((v) => withoutSpec.every((k) => v[k] === selected[k]) && v.available) ||
        variants.find((v) => withoutSpec.every((k) => v[k] === selected[k])) ||
        null
      );
    }
    return null;
  };

  const getSpecFromVariant = (variant, optionIndex) => {
    if (!variant) return '';
    if (optionIndex && variant[optionIndex]) return String(variant[optionIndex]);
    // Fallback: pick the option value that looks like Spec.
    for (const key of ['option1', 'option2', 'option3']) {
      const val = variant[key];
      if (typeof val === 'string' && (POM_LABELS.test(val) || /^material:/i.test(val))) {
        return val;
      }
    }
    return '';
  };

  const updateSpecsForSection = (section, variantFromEvent) => {
    const panel = section.querySelector('[data-dj-specs]');
    if (!panel) return;
    const list = panel.querySelector('[data-dj-spec-list]');
    if (!list) return;

    const optionIndex = panel.dataset.djSpecOptionIndex || '';
    const hasMf = panel.dataset.djHasMetafieldMaterials === 'true';
    const variants = getVariants(section);
    const selected = getSelectedOptions(section);

    // Prefer a match from the live radios. Spec is a hidden linked option and
    // is often stale after a size click — event.detail.variant can lag behind.
    const variant = findVariant(variants, selected) || variantFromEvent || null;
    const raw = getSpecFromVariant(variant, optionIndex);
    if (!raw) return;

    const tokens = normalizeTokens(extractSpecsPart(raw, hasMf));
    renderSpecList(list, tokens);
  };

  const sectionFromEvent = (event) => {
    const fromTarget = event.target?.closest?.('[data-section-type="product"]');
    if (fromTarget) return fromTarget;
    return document.querySelector('[data-section-type="product"]');
  };

  const scheduleSpecsUpdate = (section, variantFromEvent) => {
    if (!section?.querySelector('[data-dj-specs]')) return;
    // Run now and again after Motion finishes linked-option sync / DOM updates.
    updateSpecsForSection(section, variantFromEvent || null);
    requestAnimationFrame(() => updateSpecsForSection(section, variantFromEvent || null));
    setTimeout(() => updateSpecsForSection(section, null), 0);
    setTimeout(() => updateSpecsForSection(section, null), 50);
  };

  document.addEventListener('variant:change', (event) => {
    const section = sectionFromEvent(event);
    if (!section) return;
    scheduleSpecsUpdate(section, event.detail?.variant || null);
  });

  // Motion also fires a namespaced CustomEvent on the product section.
  document.addEventListener('variantChange', (event) => {
    const section = event.target?.closest?.('[data-section-type="product"]') || sectionFromEvent(event);
    if (!section) return;
    scheduleSpecsUpdate(section, event.detail?.variant || null);
  });

  // Size/color changes — Spec radios often stay on the previous size, so we
  // resolve measurements from size + color ourselves.
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!input?.matches?.('[data-variant-input]')) return;
    const section = input.closest('[data-section-type="product"]');
    scheduleSpecsUpdate(section, null);
  });

  // Label/chip clicks: ensure we refresh even if change is swallowed.
  document.addEventListener('click', (event) => {
    const chip = event.target?.closest?.(
      '.dj-variant-picker .variant-input, .dj-variant-picker label, .dj-option-slot--size label, .dj-option-slot--color label'
    );
    if (!chip) return;
    const section = chip.closest('[data-section-type="product"]');
    if (!section?.querySelector('[data-dj-specs]')) return;
    setTimeout(() => updateSpecsForSection(section, null), 0);
    setTimeout(() => updateSpecsForSection(section, null), 50);
  });
})();

/* PhotoSwipe: Escape closes zoom; ensure close control is reachable above header */
(() => {
  if (window.djPswpCloseInitialized) return;
  window.djPswpCloseInitialized = true;

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelector('.pswp.pswp--open');
    if (!open) return;
    const closeBtn = open.querySelector('.pswp__button--close');
    if (closeBtn) closeBtn.click();
  });
})();

/* DJ gallery lightbox — desktop only; mobile keeps snap-carousel (no zoom overlay) */
(() => {
  if (window.djGalleryZoomInitialized) return;
  window.djGalleryZoomInitialized = true;

  const DESKTOP_MQ = '(min-width: 769px)';
  const isDesktop = () => window.matchMedia(DESKTOP_MQ).matches;

  const ensureShell = () => {
    let root = document.getElementById('DjZoomLightbox');
    if (root) return root;

    root = document.createElement('div');
    root.id = 'DjZoomLightbox';
    root.className = 'dj-zoom';
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'Product image zoom');
    root.hidden = true;
    root.innerHTML = `
      <button type="button" class="dj-zoom__close" aria-label="Close">
        <span aria-hidden="true">×</span>
      </button>
      <button type="button" class="dj-zoom__prev" aria-label="Previous image">
        <span aria-hidden="true">‹</span>
      </button>
      <div class="dj-zoom__stage">
        <img class="dj-zoom__img" alt="" draggable="false" />
      </div>
      <button type="button" class="dj-zoom__next" aria-label="Next image">
        <span aria-hidden="true">›</span>
      </button>
    `;
    document.body.appendChild(root);
    bindSwipe(root);
    return root;
  };

  let items = [];
  let index = 0;
  let lastFocus = null;
  let touchStartX = 0;
  let touchStartY = 0;
  let touchActive = false;

  const getRoot = () => ensureShell();
  const imgEl = () => getRoot().querySelector('.dj-zoom__img');

  const render = () => {
    const root = getRoot();
    const item = items[index];
    if (!item) return;
    const img = imgEl();
    img.src = item.src;
    img.alt = item.alt || '';
    root.dataset.count = String(items.length);
    root.querySelector('.dj-zoom__prev').disabled = items.length < 2;
    root.querySelector('.dj-zoom__next').disabled = items.length < 2;
  };

  const open = (list, startIndex) => {
    items = list;
    index = Math.max(0, Math.min(startIndex || 0, items.length - 1));
    lastFocus = document.activeElement;
    const root = getRoot();
    root.hidden = false;
    root.classList.add('is-open');
    document.body.classList.add('dj-zoom-open');
    render();
    root.querySelector('.dj-zoom__close').focus();
  };

  const close = () => {
    const root = document.getElementById('DjZoomLightbox');
    if (!root) return;
    root.classList.remove('is-open');
    root.hidden = true;
    document.body.classList.remove('dj-zoom-open');
    const img = imgEl();
    if (img) img.removeAttribute('src');
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
  };

  const step = (delta) => {
    if (items.length < 2) return;
    index = (index + delta + items.length) % items.length;
    render();
  };

  const collectItems = (gallery) => {
    return Array.from(gallery.querySelectorAll('[data-dj-zoom]'))
      .map((el) => ({
        src: el.getAttribute('data-zoom-src') || el.getAttribute('href') || '',
        alt: el.querySelector('img')?.getAttribute('alt') || '',
      }))
      .filter((item) => item.src);
  };

  const bindSwipe = (root) => {
    const stage = root.querySelector('.dj-zoom__stage') || root;

    stage.addEventListener(
      'touchstart',
      (event) => {
        if (!root.classList.contains('is-open') || items.length < 2) return;
        const t = event.changedTouches?.[0];
        if (!t) return;
        touchActive = true;
        touchStartX = t.clientX;
        touchStartY = t.clientY;
      },
      { passive: true }
    );

    stage.addEventListener(
      'touchend',
      (event) => {
        if (!touchActive) return;
        touchActive = false;
        const t = event.changedTouches?.[0];
        if (!t) return;
        const dx = t.clientX - touchStartX;
        const dy = t.clientY - touchStartY;
        if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
        step(dx < 0 ? 1 : -1);
      },
      { passive: true }
    );
  };

  document.addEventListener(
    'click',
    (event) => {
      const trigger = event.target.closest?.('[data-dj-gallery] [data-dj-zoom]');
      if (!trigger) return;
      const gallery = trigger.closest('[data-dj-gallery]');
      if (!gallery || gallery.getAttribute('data-zoom') === 'false') return;

      // Mobile: keep snap-carousel only — no zoom overlay, no raw-image navigation
      if (!isDesktop()) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const list = collectItems(gallery);
      const start = parseInt(trigger.getAttribute('data-index') || '0', 10) || 0;
      open(list, start);
    },
    true
  );

  document.addEventListener('click', (event) => {
    const root = document.getElementById('DjZoomLightbox');
    if (!root?.classList.contains('is-open')) return;

    if (event.target.closest('.dj-zoom__close')) {
      event.preventDefault();
      close();
      return;
    }
    if (event.target.closest('.dj-zoom__prev')) {
      event.preventDefault();
      step(-1);
      return;
    }
    if (event.target.closest('.dj-zoom__next')) {
      event.preventDefault();
      step(1);
      return;
    }
    if (event.target === root || event.target.classList?.contains('dj-zoom__stage')) {
      // backdrop / empty stage tap closes; ignore taps on the image itself
      if (event.target === root) close();
    }
  });

  document.addEventListener('keydown', (event) => {
    const root = document.getElementById('DjZoomLightbox');
    if (!root?.classList.contains('is-open')) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      close();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      step(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      step(1);
    }
  });
})();
